import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Input, Popconfirm, Progress, Checkbox } from "antd";
import useBehaviorSelectionTreeChecked from "../../../hooks/Tree/useBehaviorSelectionTreeChecked";
import useBehaviorSelectionTreeLoading from "../../../hooks/Tree/useBehaviorSelectionTreeLoading";
import {
    setConfigSelectedBehavior,
    setConfigSelectedTarget,
    setStep,
    setToEditData,
} from "../../../store/components/MarketExplorer.slice";
import {
    checkIfhasSetToDisplayOnly,
    getCountryCode,
    getDescription,
    getMarketExplorerText,
    getModuleCode,
    getOICode,
    getPackage,
    getPackageObj,
    saveTargetBuilder,
} from "../../../utils/oneintelligence/OneIntelligenceHelper";
import MarketExplorerContainer from "./MarketExplorerContainer";
import BehaviorTree from "./BehaviorTree";
import { dynamicSort, toTitleCase } from "../../../utils/MainHelper.js";
import { setBreadItems } from "../../../store/components/TreeMapReport.slice";
import useSearchInTree from "../../../hooks/Tree/useSearchInTree";
import { useState } from "react";
import TargetInputChecker from "./TargetInputChecker.js";
import useAddBrand from "../../../hooks/ScrapedKeywords/useAddBrand.js";
import TargetInputCheckerEmpty from "./TargetInputCheckerEmpty.js";
import TreeProgress from "../../General/TreeProgress.js";
import useTreeShowLowActivity from "../../General/useTreeShowLowActivity.js";

function BehaviorSelectionTree() {
    const dispatch = useDispatch();
    const [targetDetails, setTargetDetails] = useState({});

    const [searchedText, setsearchedText] = useState("");
    const [parsingData, setParsingData] = useState(false);
    const [treeData, setTreeData] = useState([]);

    const {
        configuration: { toEditData, selectedTarget, selectedBehavior },
        behaviorTreeData,
    } = useSelector((state) => state.marketExplorer);

    const {
        generalMarketCategoriesData,
        audienceSpecificCategoriesData,
        categoryDeepDiveData,
    } = useSelector((state) => state.reports);

    const { selectedKeyword, checkedKeys, setselectedKeyword, setcheckedKeys } =
        useBehaviorSelectionTreeChecked();
    const { isLoading, forceRefresh, setForceRefresh, percentage } =
        useBehaviorSelectionTreeLoading();

    const { loading: loadingAddBrand, addBrand } = useAddBrand();

    const clearSelected = () => {
        dispatch(setConfigSelectedTarget(null));
        dispatch(setConfigSelectedBehavior(null));
        dispatch(
            setToEditData({
                data: null,
                mode: "",
            })
        );
    };

    const moduleCode = getModuleCode();
    const OICode = getOICode();

    const isNoNeedToFilter = () =>
        ["insights", "systemofcare", "patient_marketscan"].includes(moduleCode);

    useEffect(() => {
        setParsingData(true);
        let timeout = null;
        if (selectedBehavior && behaviorTreeData && moduleCode && selectedTarget) {
            setTimeout(async () => {
                const tempData = (() => {
                    if (
                        ["insights", "patient_marketscan"].includes(moduleCode) &&
                        OICode === "pharma"
                    ) {
                        return (
                            selectedBehavior?.children || selectedBehavior?.tempChildren || []
                        );
                    }
                    if (
                        ![
                            "hcp_insights",
                            "veterinaryinsights",
                            "hcp_attributes",
                            "hcp_marketscan",
                            "icd10",
                            "expertmedia",
                        ].includes(moduleCode) &&
                        OICode === "pharma"
                    )
                        return (
                            selectedBehavior?.children || selectedBehavior?.tempChildren || []
                        );
                    return behaviorTreeData;
                })();
                const filtered = await checkIfhasSetToDisplayOnly(
                    tempData,
                    selectedTarget.code,
                    selectedTarget
                )
                await setTreeData(
                    isNoNeedToFilter()
                        ? tempData
                        : filtered
                );
                setParsingData(false);
            }, 100);
        } else {
            setTreeData(behaviorTreeData);
            setParsingData(false);
        }
        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [selectedBehavior, behaviorTreeData, moduleCode, selectedTarget]);

    const hasData =
        generalMarketCategoriesData.length ||
        audienceSpecificCategoriesData.length ||
        categoryDeepDiveData.length;

    const onSaveTargets = () => {
        saveTargetBuilder(selectedKeyword);
        dispatch(setBreadItems({ mode: "back", idx: 0 }));
    };

    useEffect(() => {
        let newList = checkedKeys.sort(dynamicSort("lvl", true));
        const checkAllRecursive = (items) => {
            items.forEach((child) => {
                newList = newList.filter((item) => item.parentKey !== child.key);
                const childList = child.children || child.tempChildren;
                if (childList && childList.length) {
                    checkAllRecursive(childList);
                }
            });
        };
        checkAllRecursive([...newList]);
        setselectedKeyword(newList);
    }, [checkedKeys]);

    const { searchedData, loadingSearch } = useSearchInTree(
        searchedText,
        treeData,
        false
    );

    const { showLowActivity, lowActComponent } = useTreeShowLowActivity(selectedTarget)

    const toDisplayData = useMemo(() => {
        const display = searchedText ? searchedData : treeData;
        if (!(selectedTarget?.code || selectedTarget?.key)?.includes("mokb")) return display
        if (showLowActivity) return display;
        return display.reduce((acc, d) => {
            const children = (d?.children || []).filter(
                (child) => !child.lowActivity
            );
            if (children.length) acc.push(d);
            return acc;
        }, []);
    }, [searchedText, selectedTarget, searchedData, treeData, showLowActivity]);


    const displayAddnewMOKB = () => {
        if (["healthcarewhitespaces", "veterinaryjourney"].includes(moduleCode)) return null;
        if (["mokb_marketexplorer", "mokb_marketexplorer_main"].includes(selectedTarget.code))
            return (
                <>
                    {" or "}
                    <Button
                        type="link"
                        style={{ lineHeight: "unset", fontSize: "11px", padding: "0" }}
                        onClick={() => {
                            window.open("https://oneknowledge.sqreemtech.com/");
                        }}
                    >
                        <span style={{ textDecoration: "underline" }}>
                            Add New to My Own Knowledge Base
                        </span>
                    </Button>
                </>
            );
        return null;
    };

    const hasLoading = isLoading || loadingSearch || parsingData
    return (
        <div
            className={`reportBuilder-leftside_targets-behavior ${["healthcarewhitespaces", "veterinaryjourney"].includes(moduleCode) &&
                ["mokb_marketexplorer", "mokb_marketexplorer_main"].includes(selectedTarget.code)
                ? "mokb"
                : ""
                }`}
            style={{ height: "calc(100% - 22mm)" }}
        >
            <MarketExplorerContainer
                className="reportBuilder-leftside_targets-selection"
                title={getMarketExplorerText(selectedTarget)}
                subTitle={
                    <>
                        {getDescription(selectedTarget)}
                        {displayAddnewMOKB()}
                    </>
                }
                search={
                    <>
                        <Input.Search
                            placeholder="Search here..."
                            style={{ width: "100%", margin: "1mm 0" }}
                            value={searchedText}
                            onChange={(e) => {
                                setsearchedText(e.target.value);
                            }}
                            onSearch={(e) => {
                                setsearchedText(e);
                            }}
                            allowClear
                        />
                        {hasLoading ? null : lowActComponent}
                        <TreeProgress percentage={percentage} />
                    </>
                }
            >
                <BehaviorTree
                    selectedKeyword={selectedKeyword}
                    setselectedKeyword={setselectedKeyword}
                    checkedKeys={checkedKeys}
                    setcheckedKeys={setcheckedKeys}
                    treeData={toDisplayData}
                    isLoading={hasLoading}
                    searchedText={searchedText}
                    setTargetDetails={setTargetDetails}
                />
            </MarketExplorerContainer>
            <div
                className="d-flex justify-content-end"
                style={{
                    padding: "2mm 1mm",
                }}
            >
                <Button
                    style={{
                        marginRight: "1mm",
                    }}
                    onClick={() => {
                        if (toEditData.mode) {
                            dispatch(setStep(3));
                        } else {
                            dispatch(setStep(1));
                        }
                        clearSelected();
                    }}
                >
                    Cancel
                </Button>
                {hasData ? (
                    <Popconfirm
                        placement="bottomLeft"
                        title="The existing report will be removed. Are you sure you want to continue?"
                        onConfirm={onSaveTargets}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="primary" disabled={!selectedKeyword.length}>
                            Set Target
                        </Button>
                    </Popconfirm>
                ) : (
                    <Button
                        onClick={onSaveTargets}
                        type="primary"
                        disabled={!selectedKeyword.length}
                    >
                        Set Target
                    </Button>
                )}
            </div>
            {targetDetails?.fullPath ? (
                <TargetInputChecker
                    title="Add Brand"
                    description="Enter your target to add brand."
                    path={targetDetails?.fullPath}
                    loading={loadingAddBrand}
                    onCancel={() => {
                        setTargetDetails({});
                    }}
                    onSave={(inputValue) => {
                        const newTargetDetails = { ...targetDetails }
                        newTargetDetails.fullPath = `${targetDetails?.fullPath
                            }\\${toTitleCase(inputValue.trim())}`;
                        addBrand(newTargetDetails, () => {
                            setForceRefresh(!forceRefresh);
                            setTargetDetails({});
                        });
                    }}
                    onClickRedirect={(inputValue) => {
                        const pathValue = `${targetDetails?.fullPath}\\${toTitleCase(
                            inputValue.trim()
                        )}`.replaceAll("&", "**");

                        window.open(
                            `https://oneknowledge.sqreemtech.com/#/?pval=${pathValue}&htype=${targetDetails?.healthcareCategoryType}`
                        );
                    }}
                />
            ) : null}

            {!treeData.length &&
                ["healthcarewhitespaces", "veterinaryjourney"].includes(moduleCode) &&
                ["mokb_marketexplorer", "mokb_marketexplorer_main"].includes(selectedTarget.code) ? (
                <TargetInputCheckerEmpty />
            ) : null}
        </div>
    );
}
export default BehaviorSelectionTree;
